import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

// Token storage keys
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

console.log('Shared API Client - API URL being used:', API_URL);

// Track refresh promise to prevent multiple simultaneous refresh attempts
let refreshPromise = null;

// Create shared axios instance with base URL
const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add interceptor to include the auth token in requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('API Client - Request interception error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration with automatic refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    console.log('API Client - Response error:', error.response?.status, error.response?.statusText);
    
    // If error is 401 Unauthorized and we haven't tried to refresh already
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      console.log('API Client - 401 detected, attempting token refresh...');
      
      try {
        // If there's already a refresh in progress, wait for it
        if (refreshPromise) {
          console.log('API Client - Refresh already in progress, waiting...');
          await refreshPromise;
          
          // Get the new token and retry
          const newToken = localStorage.getItem(TOKEN_KEY);
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        }
        
        // Start a new refresh
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (refreshToken) {
          console.log('API Client - Refresh token found, making refresh request...');
          
          // Create and store the refresh promise to prevent duplicate refreshes
          refreshPromise = axios.post(`${API_URL}/auth/refresh-token`, {
            refreshToken
          }, {
            headers: { 'Content-Type': 'application/json' }
          }).finally(() => {
            // Clear the promise when done
            refreshPromise = null;
          });
          
          const response = await refreshPromise;
          
          if (response.data.token) {
            console.log('API Client - Token refresh successful, updating tokens...');
            
            // Store the new tokens
            localStorage.setItem(TOKEN_KEY, response.data.token);
            if (response.data.refreshToken) {
              localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
            }
            
            // Update default header and retry original request
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            originalRequest.headers['Authorization'] = `Bearer ${response.data.token}`;
            
            console.log('API Client - Retrying original request with new token...');
            return apiClient(originalRequest);
          }
        } else {
          console.log('API Client - No refresh token available, cannot refresh');
        }
      } catch (refreshError) {
        console.error('API Client - Token refresh failed:', refreshError);
        
        // Clear the refresh promise on error
        refreshPromise = null;
        
        // Only logout if refresh actually failed (not network issues)
        if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
          console.log('API Client - Refresh token invalid, logging out...');
          
          // Clear auth data
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem('user');
          
          // Redirect to login page
          window.location.href = '/login';
        } else {
          console.log('API Client - Network error during refresh, keeping session');
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
